import React from 'react';
import { Link } from 'react-router-dom';
import { useGlobalContext } from '../../context/GlobalContext';

const Footer = () => {
  const { globalSettings: settings } = useGlobalContext();

  return (
    <footer className="bg-white/5 backdrop-blur-xl border-t border-white/10 text-text-primary py-14 relative overflow-hidden">
      {/* Decorative Gradient Top Border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand & Mission */}
          <div className="md:col-span-2 space-y-6">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center p-1 group-hover:border-accent/50 transition-smooth">
                <img
                  src={settings?.about_hero_pic || "/assets/images/Sudhan_2026_v3.png"}
                  alt="Sudhan R Portfolio Logo"
                  className="w-full h-full object-cover rounded-lg"
                  width={48}
                  height={48}
                />
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl tracking-tight text-text-primary">
                   Sudhan R
                </h3>
                <p className="text-accent text-sm font-medium tracking-wider uppercase">
                  Space & Tech Researcher
                </p>
              </div>
            </Link>
            <p className="text-text-secondary text-lg max-w-md leading-relaxed">
              Crafting tech-driven solutions at the intersection of space research, data science, and education. 
              Let’s collaborate on meaningful innovations—grounded on Earth, aiming for the stars.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-bold text-text-primary text-lg mb-6">Discovery</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/about" className="text-text-secondary hover:text-accent transition-smooth flex items-center group">
                  <span className="w-0 group-hover:w-2 h-px bg-accent mr-0 group-hover:mr-2 transition-all duration-300"></span>
                  About Story
                </Link>
              </li>
              <li>
                <Link to="/projects" className="text-text-secondary hover:text-accent transition-smooth flex items-center group">
                  <span className="w-0 group-hover:w-2 h-px bg-accent mr-0 group-hover:mr-2 transition-all duration-300"></span>
                  Research Projects
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-text-secondary hover:text-accent transition-smooth flex items-center group">
                  <span className="w-0 group-hover:w-2 h-px bg-accent mr-0 group-hover:mr-2 transition-all duration-300"></span>
                  Technical Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-text-secondary hover:text-accent transition-smooth flex items-center group">
                  <span className="w-0 group-hover:w-2 h-px bg-accent mr-0 group-hover:mr-2 transition-all duration-300"></span>
                  Get In Touch
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-heading font-bold text-text-primary text-lg mb-6">Connect</h4>
            <ul className="space-y-4 text-text-secondary">
              <li>
                <a href={`mailto:${settings?.contact_email || 'sudhan.r.web@gmail.com'}`} className="hover:text-accent transition-smooth break-all">
                  {settings?.contact_email || 'sudhan.r.web@gmail.com'}
                </a>
              </li>
              <li>
                <a href={`tel:${(settings?.contact_phone || '919940980978').replace(/[-+ \(\)]/g, '')}`} className="hover:text-accent transition-smooth">
                  {settings?.contact_phone || '+91 99409 80978'}
                </a>
              </li>
              <li className="pt-4 flex flex-wrap items-center gap-2.5">
                <a href={settings?.social_linkedin || "https://www.linkedin.com/in/sudhan23082004/"} aria-label="LinkedIn Profile" target="_blank" rel="noopener noreferrer" className="w-[4.5rem] h-7 sm:h-8 flex items-center justify-center bg-white rounded-md transition-smooth hover:-translate-y-1 hover:shadow-lg hover:shadow-white/20 px-1.5 py-1">
                   <img src="https://image2url.com/r2/default/images/1772367934424-eb0ef44b-7240-4980-94c2-882d2c724593.png" alt="LinkedIn" width={24} height={24} className="w-full h-full object-contain" />
                </a>
                <a href={settings?.social_github || "https://github.com/Sudhan-23082004"} aria-label="GitHub Profile" target="_blank" rel="noopener noreferrer" className="w-[4.5rem] h-7 sm:h-8 flex items-center justify-center bg-white rounded-md transition-smooth hover:-translate-y-1 hover:shadow-lg hover:shadow-white/20 px-1.5 py-1">
                   <img src="https://image2url.com/r2/default/images/1772367960629-b9edbb92-45d5-487c-8538-65268697e0c3.png" alt="GitHub" width={24} height={24} className="w-full h-full object-contain" />
                </a>
                <a href={settings?.social_orcid || "https://orcid.org/0009-0007-0461-2150"} aria-label="ORCID Profile" target="_blank" rel="noopener noreferrer" className="w-[4.5rem] h-7 sm:h-8 flex items-center justify-center bg-white rounded-md transition-smooth hover:-translate-y-1 hover:shadow-lg hover:shadow-white/20 px-1.5 py-1">
                   <img src="https://orcid.org/assets/vectors/orcid.logo.svg" alt="ORCID" width={24} height={24} className="w-full h-full object-contain" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-10 mt-10 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
            <p className="text-white/40 text-sm">
              <time dateTime={new Date().toISOString().split('T')[0]}>© {new Date().getFullYear()}</time> <span className="author" itemProp="author">Sudhan R</span>. All rights reserved.
            </p>
            <div className="hidden md:block w-1.5 h-1.5 rounded-full bg-white/10"></div>
            <img
              src="https://api.visitorbadge.io/api/visitors?path=https%3A%2F%2Fgithub.com%2FSudhan-23082004%2FSudhan.git&label=Visitors&countColor=%230EA5E9"
              alt="Visitor Counter"
              width={100}
              height={24}
              className="h-6 w-auto opacity-80"
            />
          </div>
          
          <div className="flex items-center space-x-8 text-sm font-medium tracking-wide">
             <span className="text-text-muted italic">Advancing Space Research through Code</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
